import { z } from 'zod';
import { TenantId } from './ids.js';
import { CurrencyCode, Timezone } from './primitives.js';
import { DEFAULT_LOCALE, Locale } from './i18n.js';
/**
 * Where a tenant is in its life. This enum is load-bearing: `authority` on every
 * availability response is derived from it (spec 4.4), so it is the difference
 * between an agent that promises a time and an agent that hedges.
 */
export const TenantStatus = z.enum([
    'onboarding', // being set up; the partner is not connected yet
    'importing', // dual-running with the old system during cutover
    'live', // we hold the book
    'paused', // temporarily not answering
    'offboarded',
]);
export const Tenant = z.object({
    id: TenantId,
    slug: z.string().min(1),
    name: z.string().min(1),
    timezone: Timezone,
    currency: CurrencyCode,
    status: TenantStatus,
});
/**
 * What the partner may rely on (agreement section 3). Every field is **computed**,
 * never stored as free text: the agreement is explicit that declaring a
 * capability you do not have is the only way to break the integration, and the
 * reliable way to avoid that is to make the declaration a function of reality.
 */
export const Capabilities = z.object({
    availability: z.enum(['live', 'approximate', 'none']),
    reserve: z.enum(['direct', 'link', 'none']),
    confirm: z.enum(['instant', 'webhook', 'email']),
    clients: z.enum(['full', 'basic', 'none']),
    cancel: z.enum(['direct', 'link', 'none']),
});
/**
 * `live` only when this system holds the book. `importing` means another system
 * can still take a booking, so anything we say may already be stale — the agent
 * should hedge, and it will, because we told it the truth.
 */
export function authorityFor(status) {
    return status === 'live' ? 'live' : 'approximate';
}
/**
 * The capability declaration for a tenant.
 *
 * `reserve` degrades with the tenant's state rather than being switched off by
 * hand: a paused or offboarded tenant cannot be booked into, and the agent
 * routes around it instead of failing mid-conversation.
 */
export function capabilitiesFor(status) {
    const bookable = status === 'live' || status === 'importing';
    return {
        availability: status === 'paused' || status === 'offboarded' ? 'none' : authorityFor(status),
        reserve: bookable ? 'direct' : 'none',
        confirm: 'instant',
        clients: status === 'offboarded' ? 'none' : 'full',
        cancel: bookable ? 'direct' : 'none',
    };
}
/** Per-tenant scheduling knobs. Defaults are LoveSpa's, so the port is a no-op. */
export const TenantConfig = z.object({
    /** Start granularity for offered slots, in minutes. */
    slot_grid_min: z.number().int().min(5).max(120).default(15),
    /** How far ahead availability may be asked about. */
    horizon_days: z.number().int().min(1).max(365).default(60),
    /** Ceiling on a hold's requested TTL. */
    max_hold_seconds: z.number().int().min(60).max(3600).default(1800),
    /** How far `alternatives` may wander from the ask, in days. */
    alternatives_window_days: z.number().int().min(1).max(30).default(7),
    /** Monotonic; bumped by any write that changes the catalog (agreement S2). */
    catalog_version: z.number().int().min(0).default(1),
    /**
     * The language this tenant's content is written in when nothing else is said
     * (i18n-design §2, Domain B). It is what a localized map is keyed by on write
     * and what `localize` falls back to on read, so changing it does **not**
     * retranslate anything — it changes which language a guest who asked for
     * neither gets.
     */
    default_locale: Locale.default(DEFAULT_LOCALE),
    /**
     * The languages the tenant *offers*. Narrows negotiation — a browser asking
     * for French at a tenant that speaks English and Spanish is answered in one of
     * those two rather than being handed a French request the data cannot fill.
     *
     * Empty means "undeclared", which is not the same as "English only": the
     * catalog still decides, and a tenant who translated their menu before filling
     * in this row is served in the language they translated into.
     */
    supported_locales: z.array(Locale).default([]),
});
/**
 * The tenant's default locale, read from a config document that may predate the
 * field.
 *
 * `tenant_config.config` is versioned JSONB and the store *casts* rather than
 * parses it (there is one shape today and re-parsing it on every availability
 * request would be work on the hot path for no obligation). So a config written
 * before migration 0012 has no `default_locale` at all, and every reader needs
 * the same answer to that. One function, rather than eleven `?? 'en'`s.
 */
export function defaultLocaleOf(config) {
    const raw = config?.default_locale;
    return typeof raw === 'string' && Locale.safeParse(raw).success ? raw : DEFAULT_LOCALE;
}
/** The tenant's declared `supported_locales`, `[]` when the field is absent. */
export function supportedLocalesOf(config) {
    const raw = config?.supported_locales;
    if (!Array.isArray(raw))
        return [];
    return raw.filter((l) => typeof l === 'string' && Locale.safeParse(l).success);
}
//# sourceMappingURL=tenant.js.map